/**
 * The reservations store's reducer and actions (RESERVATIONS_V2_PLAN.md §4.6, decisions 35 and 54). It has no saga:
 * the flows call the gateway and record what it answered here. A reducer never reads a clock, so every `loadedAt`
 * is given by the caller. A load that starts keeps the old data on screen until the new answer lands, and a load
 * that fails keeps it too, with the refusal beside it. An answer that holds what the store already holds keeps the
 * stored objects (each booking, a day's ids and summary), so the minute's reload that found nothing new hands no view a
 * new object and redraws nothing (plan §4.6, rule 8).
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
// The per-function module: Node's ESM loader (the node:test runs) cannot read a named export from lodash's CommonJS build.
import isEqual from "lodash/isEqual";

import { supportLogout, userLogout } from "../../redux-store/slices/LoginSlice/actions";
import type { ReservationRefusal } from "../gateway/refusals";
import type { PartnerOutletRow } from "../partners/enabled";
import type { DaySummary, Reservation, ReservationSettings } from "../model/types";
import type { DayEntry, LoadEntry, ReservationsState } from "./types";

/** The store with nothing loaded: what it starts as and what a sign-out returns it to. */
export const EMPTY_RESERVATIONS: ReservationsState = {
  byId: {},
  fetched: {},
  days: {},
  settings: {},
  partnerOutlets: {},
  interestRequested: false,
};

/** The key of one branch's one day in `ReservationsState.days`: `"<branchId>|<date>"`. The branch id never contains a bar. */
export function dayKeyOf(branchId: string, date: string): string {
  return `${branchId}|${date}`;
}

/** Which day of which branch an action is about; `date` is `YYYY-MM-DD`. */
interface DayTarget {
  /** The branch the day belongs to. */
  branchId: string;
  /** The day, `YYYY-MM-DD`. */
  date: string;
}

/** The payload of `dayLoaded`: the day, its answer, and when the answer landed. */
interface DayLoadedPayload extends DayTarget {
  /** The day's slots. */
  summary: DaySummary;
  /** The day's bookings in the list's order. */
  reservations: Reservation[];
  /** When the answer landed, in epoch milliseconds. */
  loadedAt: number;
}

/** The payload of `dayLoadFailed`: the day and why the load was refused. */
interface DayLoadFailedPayload extends DayTarget {
  /** Why the load was refused. */
  refusal: ReservationRefusal;
}

/** The payload of every `…LoadStarted` action for a per-branch value. */
interface BranchTarget {
  /** The branch the load is for. */
  branchId: string;
}

/** The payload of `settingsLoaded`: the branch, its settings, and when they landed. */
interface SettingsLoadedPayload extends BranchTarget {
  /** The branch's settings. */
  settings: ReservationSettings;
  /** When the answer landed, in epoch milliseconds. */
  loadedAt: number;
}

/** The payload of `partnerOutletsLoaded`: the branch, its outlet rows, and when they landed. */
interface PartnerOutletsLoadedPayload extends BranchTarget {
  /** The branch's partner outlet rows. */
  outlets: PartnerOutletRow[];
  /** When the answer landed, in epoch milliseconds. */
  loadedAt: number;
}

/** The payload of `settingsLoadFailed` and `partnerOutletsLoadFailed`: the branch and why the load was refused. */
interface BranchLoadFailedPayload extends BranchTarget {
  /** Why the load was refused. */
  refusal: ReservationRefusal;
}

/** The day's entry in the draft state, created empty when the day has never been asked for. */
function dayEntryToChange(state: ReservationsState, target: DayTarget): DayEntry {
  const key = dayKeyOf(target.branchId, target.date);
  const existing = state.days[key];
  if (existing !== undefined) {
    return existing;
  }
  const created: DayEntry = { ids: [], loading: false };
  state.days[key] = created;
  return created;
}

/**
 * Puts a booking into a table of bookings (`byId` or `fetched`), keeping the stored object when the new one holds the
 * same data: the gateway reads every answer into new objects, so without this each reload would hand every card a new
 * booking and draw it again.
 */
function storeBooking(table: Record<string, Reservation>, reservation: Reservation): void {
  const stored = table[reservation.id];
  if (stored === undefined || !isEqual(stored, reservation)) {
    table[reservation.id] = reservation;
  }
}

/** The branch's entry in a per-branch table of loads, created empty when it has never been asked for. */
function loadEntryToChange<Value>(entries: Record<string, LoadEntry<Value>>, branchId: string): LoadEntry<Value> {
  const existing = entries[branchId];
  if (existing !== undefined) {
    return existing;
  }
  const created: LoadEntry<Value> = { loading: false };
  entries[branchId] = created;
  return created;
}

/** The reservations store: its reducer and the action creators exported below. */
const slice = createSlice({
  name: "reservations",
  initialState: EMPTY_RESERVATIONS,
  reducers: {
    /** A day's load began: it shows as loading, and whatever the day already held stays until the answer lands. */
    dayLoadStarted: (state, action: PayloadAction<DayTarget>) => {
      dayEntryToChange(state, action.payload).loading = true;
    },
    /**
     * A day's list landed: each booking is stored, and the day keeps their ids in the list's order. A booking, the ids
     * and the summary that equal what is stored keep the stored objects, so only the load time changes.
     */
    dayLoaded: (state, action: PayloadAction<DayLoadedPayload>) => {
      const { summary, reservations, loadedAt } = action.payload;
      for (const reservation of reservations) {
        storeBooking(state.byId, reservation);
      }
      const entry = dayEntryToChange(state, action.payload);
      if (entry.summary === undefined || !isEqual(entry.summary, summary)) {
        entry.summary = summary;
      }
      const ids = reservations.map((reservation) => reservation.id);
      if (!isEqual(entry.ids, ids)) {
        entry.ids = ids;
      }
      entry.loadedAt = loadedAt;
      entry.loading = false;
      delete entry.refusal;
      // `fetched` stays as it is: a list row does not know `orderOpen`, so it must not replace a fetched copy that does.
    },
    /** A day's load was refused: the last good data stays, with the refusal beside it. */
    dayLoadFailed: (state, action: PayloadAction<DayLoadFailedPayload>) => {
      const entry = dayEntryToChange(state, action.payload);
      entry.loading = false;
      entry.refusal = action.payload.refusal;
    },
    /**
     * A booking arrived from a fetch or a write. It goes into `byId`, and into `fetched` too when it was read from a
     * fetched document; an equal stored booking keeps its object. It is not added to any day's ids: the write's flow
     * reloads its day.
     */
    reservationStored: (state, action: PayloadAction<{ reservation: Reservation }>) => {
      const { reservation } = action.payload;
      storeBooking(state.byId, reservation);
      if (reservation.readFrom === "fetchedDocument") {
        storeBooking(state.fetched, reservation);
      }
    },
    /** A branch's settings load began; the settings it already holds stay. */
    settingsLoadStarted: (state, action: PayloadAction<BranchTarget>) => {
      loadEntryToChange(state.settings, action.payload.branchId).loading = true;
    },
    /** A branch's settings landed. */
    settingsLoaded: (state, action: PayloadAction<SettingsLoadedPayload>) => {
      const entry = loadEntryToChange(state.settings, action.payload.branchId);
      entry.value = action.payload.settings;
      entry.loadedAt = action.payload.loadedAt;
      entry.loading = false;
      delete entry.refusal;
    },
    /** A branch's settings load was refused; the settings it already holds stay. */
    settingsLoadFailed: (state, action: PayloadAction<BranchLoadFailedPayload>) => {
      const entry = loadEntryToChange(state.settings, action.payload.branchId);
      entry.loading = false;
      entry.refusal = action.payload.refusal;
    },
    /** A branch's partner outlets load began; the rows it already holds stay. */
    partnerOutletsLoadStarted: (state, action: PayloadAction<BranchTarget>) => {
      loadEntryToChange(state.partnerOutlets, action.payload.branchId).loading = true;
    },
    /** A branch's partner outlet rows landed. */
    partnerOutletsLoaded: (state, action: PayloadAction<PartnerOutletsLoadedPayload>) => {
      const entry = loadEntryToChange(state.partnerOutlets, action.payload.branchId);
      entry.value = action.payload.outlets;
      entry.loadedAt = action.payload.loadedAt;
      entry.loading = false;
      delete entry.refusal;
    },
    /** A branch's partner outlets load was refused; the rows it already holds stay. */
    partnerOutletsLoadFailed: (state, action: PayloadAction<BranchLoadFailedPayload>) => {
      const entry = loadEntryToChange(state.partnerOutlets, action.payload.branchId);
      entry.loading = false;
      entry.refusal = action.payload.refusal;
    },
    /** The interest page's request for access was sent: the page says so until sign-out (decision 87). */
    interestRequestSent: (state) => {
      state.interestRequested = true;
    },
  },
  extraReducers: (builder) => {
    // Decision 54: the next person on the terminal must not see the last one's bookings, so both sign-outs empty the store.
    builder.addCase(userLogout, () => EMPTY_RESERVATIONS).addCase(supportLogout, () => EMPTY_RESERVATIONS);
  },
});

export const {
  dayLoadStarted,
  dayLoaded,
  dayLoadFailed,
  reservationStored,
  settingsLoadStarted,
  settingsLoaded,
  settingsLoadFailed,
  partnerOutletsLoadStarted,
  partnerOutletsLoaded,
  partnerOutletsLoadFailed,
  interestRequestSent,
} = slice.actions;

/** The reservations store's reducer, registered in both apps' root reducers. */
export const reservationsReducer = slice.reducer;
